import type { LexiconEntry } from './lexicon-schema.js'
import { LEXICON_ENTRIES } from './lexicon-entries.js'
import { BORROWED_LEXICON_ENTRIES } from './lexicon-borrowed-entries.js'
import { CROSSLINK_LEXICON_ENTRIES } from './lexicon-crosslink-entries.js'
import { EAR_TRAINING_LEXICON_ENTRIES } from './lexicon-ear-training-entries.js'
import { FUNCTION_LEXICON_ENTRIES } from './lexicon-function-entries.js'
import { FUNCTION_QUIZ_LEXICON_ENTRIES } from './lexicon-function-quiz-entries.js'
import { REHARM_LEXICON_ENTRIES } from './lexicon-reharm-entries.js'
import { RHYTHM_LEXICON_ENTRIES } from './lexicon-rhythm-entries.js'

/**
 * Frozen map of all lexicon entries keyed by id. Built at module load from
 * `LEXICON_ENTRIES` (Phase-1 starter set: cadences/modes/progressions/transitions),
 * `BORROWED_LEXICON_ENTRIES` (borrowed-chord modal-interchange entries, S3b),
 * `FUNCTION_LEXICON_ENTRIES` (Phase 1.5 diatonic-function entries),
 * `FUNCTION_QUIZ_LEXICON_ENTRIES` (Phase 2 Tier 1 Function Quiz — three answer
 * categories + prompt + session-end footer copy),
 * `REHARM_LEXICON_ENTRIES` (Phase 2 Tier 2 Reharm Lab — five substitution
 * techniques + empty-state copy),
 * `CROSSLINK_LEXICON_ENTRIES` (v1 tool inter-linking — static cross-tool
 * footer entries + 1 contextual borrowed-♭VII → Mixolydian breadcrumb),
 * `EAR_TRAINING_LEXICON_ENTRIES` (Phase 2 Tier 2 Ear Training Suite — 12
 * interval entries + 7 chord-quality entries + 1 welcome-intro entry), and
 * `RHYTHM_LEXICON_ENTRIES` (Phase 3 Tier 3 Rhythm Trainer v1 — 1 tool-level +
 * 2 sub-mode + 5 meters + 4 subdivisions + 2 feedback wrappers).
 * New entries should land in the appropriate sibling file so the polished
 * starter set stays attributable to its author.
 */
const ALL_ENTRIES: LexiconEntry[] = [
  ...LEXICON_ENTRIES,
  ...BORROWED_LEXICON_ENTRIES,
  ...FUNCTION_LEXICON_ENTRIES,
  ...FUNCTION_QUIZ_LEXICON_ENTRIES,
  ...REHARM_LEXICON_ENTRIES,
  ...CROSSLINK_LEXICON_ENTRIES,
  ...EAR_TRAINING_LEXICON_ENTRIES,
  ...RHYTHM_LEXICON_ENTRIES,
]

export const LEXICON: Readonly<Record<string, LexiconEntry>> = Object.freeze(
  Object.fromEntries(ALL_ENTRIES.map(entry => [entry.id, entry]))
)

/**
 * Returns the lexicon entry for `id`, or null when the id is unknown. The
 * null return is deliberate: surfaces should gracefully hide the label rather
 * than render a placeholder when the analyzer emits an id the lexicon hasn't
 * shipped yet.
 */
export const getLexiconEntry = (id: string): LexiconEntry | null => LEXICON[id] ?? null
